/**
 * App bootstrap — runs once at app start.
 *
 * Responsibilities:
 *   - When `VITE_API_MODE === 'mock'`, dynamically import MSW and start the
 *     service-worker so the API client's fetches are intercepted locally.
 *
 * The dynamic import keeps `msw` out of the production bundle when
 * `VITE_API_MODE === 'real'` (Vite tree-shakes the unused branch).
 *
 * `bootstrap()` is idempotent — multiple calls share the same cached
 * promise so concurrent callers don't double-start the worker.
 */

let bootstrapPromise: Promise<void> | null = null;

export function bootstrap(): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = doBootstrap();
  return bootstrapPromise;
}

async function doBootstrap(): Promise<void> {
  const mode = (import.meta as ImportMeta).env?.VITE_API_MODE ?? 'real';
  if (mode !== 'mock') return;

  // Dynamic import keeps MSW out of the production bundle when not used.
  const [{ setupWorker }, { handlers }] = await Promise.all([
    import('msw/browser'),
    import('./mocks/handlers'),
  ]);

  const worker = setupWorker(...handlers);
  await worker.start({
    // Don't warn loudly about the absence of a service worker file in dev;
    // we copy the worker file via the MSW init step (below in README).
    onUnhandledRequest: 'bypass',
    quiet: true,
  });

  // eslint-disable-next-line no-console
  console.info('[patient-portal] MSW mock handlers active.');
}
